import debounce from 'lodash.debounce'
import {MediaControl as ClapprMediaControl} from '@clappr/plugins'
import throttle from 'raf-throttle'
import {
  type PlaybackSettings,
  type AttributeMap,
  type EventMap,
  type TemplateStringifier,
  Utils,
  Browser,
  type SupportedVersion,
  Playback,
  template
} from '@clappr/core'
import {IFrameProvider} from 'common/types/provider'
import {
  Errors,
  type MediaControlConfig,
  AdvertisementBlockType
} from 'common/api/records'
import type {
  Advertisement,
  AdvertisementEvent
} from 'common/plugins/advertisement'
import type {Docking} from 'common/plugins/docking'
import type {Suggest} from 'common/plugins/suggest'
import type {MultiPlayer} from 'common/plugins/multiplayer'
import type {WatchNext} from 'common/plugins/watch-next'
import {
  type Options,
  PLAY_IN_VISIBLE_AREA,
  UNMUTE_IN_VISIBLE_AREA,
  PLAY_IN_VISIBLE_PAGE
} from 'common/types/options'
import {injectStyle, loadStyle} from 'utils/dom'
import {fade, convertNumberToHex, isDark} from 'utils/colors'
import {Events} from 'common/types/events'
import {MOBILE_MAX_WIDTH} from 'common/constants'
import * as skins from './skins'

type GetClassNames = (params: {
  width: number
  height: number
  suggestHeight: number | null
  docking: boolean
  title: boolean
  likes: boolean
  episodes: boolean
}) => [string, boolean][]

const WHITE_NUMBER = 16777215
const WHITE_STRING = 'rgba(255, 255, 255, 1.00)'

export class MediaControl<T extends Options> extends ClapprMediaControl<T> {
  public static FULLSCREEN_THROTTLE_TIMOUT = 500
  public static RESIZE_THROTTLE_TIMOUT = 60
  public static VOLUME = 10
  public static MEDIUM_VOLUME = 30
  public static HIGH_VOLUME = 70
  public static SYNC_VOLUME_TIMEOUT = 300
  public static END_TIMEOUT = 10

  private static template = ''
  private static classNames: Record<string, string> = {}
  private static getContainerClassNames?: GetClassNames
  public static volume: null | number = null
  public static mutedVolume: null | number = null

  public static async preload(options: Options): Promise<Options | void> {
    if (options.mediaControl && !MediaControl.template) {
      const {skinName} = options.mediaControl
      const loadSkin = (skinName && (skins as any)[skinName]) || skins.classic

      if (typeof loadSkin !== 'function') {
        return
      }

      const {
        default: template,
        classNames,
        getContainerClassNames,
        options: customOptions
      } = await loadSkin()

      if (template) {
        MediaControl.template = template
      }

      if (classNames) {
        MediaControl.classNames = classNames
      }

      MediaControl.getContainerClassNames = getContainerClassNames

      return customOptions
    }
  }

  private $liveButton!: ZeptoCollection
  private $verticalVolume!: ZeptoCollection
  private $initialSeekBarContainers!: ZeptoCollection
  private $title!: ZeptoCollection
  private $leftTime!: ZeptoCollection
  private $timePlaceholder!: ZeptoCollection
  private $endedHidden!: ZeptoCollection
  private $episodes!: ZeptoCollection
  private hasStarted = false
  private isPlaying = false
  private isStopped = false
  private isEnded = false
  private preventShowControls = false
  private displayedPosition?: string
  private displayedDuration?: string
  private displayedLeftTime?: string
  private displayedTimePlaceholder?: string

  // NOTE: hack for initial seek bar render before start playing
  protected currentPositionValue = 0
  protected currentDurationValue =
    (this.core.options.playList?.duration ?? 0) / 1000
  protected currentLeftTimeValue = this.currentDurationValue

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get docking(): Docking<T> | null {
    return this.core.getPlugin('docking') as Docking<T> | null
  }

  private get suggest(): Suggest<T> | null {
    return this.core.getPlugin('suggest') as Suggest<T> | null
  }

  private get multiPlayer(): MultiPlayer<T> | null {
    return this.core.getPlugin('multiplayer') as MultiPlayer<T> | null
  }

  private get watchNext(): WatchNext<T> | null {
    return this.core.getPlugin('watchNext') as WatchNext<T> | null
  }

  public get name(): string {
    return 'mediaControl'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get attributes(): AttributeMap {
    return {
      class: 'eplayer-skin eplayer-skin-not-started',
      'data-media-control': ''
    }
  }

  public get events(): EventMap {
    // TODO(VP-4598): Избавиться от использования классов для определения обработчика события
    // Использование дата аттрибутов имеет приоритет
    return {
      'click .eplayer-skin-toggle, [data-toggle-play]': 'togglePlayPause',
      'click [data-play-next]': 'playNext',
      'mousemove [data-play-next-hover]': 'showPlayNext',
      'mouseleave [data-play-next-hover]': 'hidePlayNext',
      'click .eplayer-skin-button-fullscreen, [data-fullscreen]':
        'toggleFullscreen',
      'click [data-seek]': 'seek',
      'click .eplayer-skin-live-clickable, [data-live]': 'backToLiveStream',
      'click .eplayer-skin-volume-pane, [data-volume]': 'onVolumeClick',
      'click .eplayer-skin-volume-button, [data-mute]': 'toggleMute',
      'click [data-share]': 'onShareClick',
      'click [data-menu]': 'onMenuClick',
      'click [data-quality-select]': 'onLevelSelectorClick',
      'click [data-subtitle-item]': 'onSubtitleClick',
      'click [data-rewind]': 'onRewindClick',
      'click .eplayer-skin-title-text, [data-title]': 'onTitleClick',
      'mousedown .eplayer-skin-volume-pane, [data-volume]': 'startVolumeDrag',
      'touchstart .eplayer-skin-volume-pane, [data-volume]': 'startVolumeDrag',
      'mousedown [data-seek]': 'startSeekDrag',
      'touchstart [data-seek]': 'startSeekDrag',
      'mousemove [data-seek]': 'mousemoveOnSeekBar',
      'mouseleave [data-seek]': 'mouseleaveOnSeekBar'
    }
  }

  public get hasAudio(): boolean {
    return !this.core.options.recordIsMute && (this.playback?.hasAudio ?? true)
  }

  private get initialVolume(): number {
    return this.options.volume ?? MediaControl.VOLUME
  }

  private get mutedVolume(): null | number {
    return MediaControl.mutedVolume
  }

  private set mutedVolume(value: null | number) {
    MediaControl.mutedVolume = value
  }

  public get params(): MediaControlConfig {
    return this.core.options.mediaControl || ({} as MediaControlConfig)
  }

  private get classNames(): Record<string, string> {
    return MediaControl.classNames
  }

  public get template(): TemplateStringifier {
    return template(MediaControl.template)
  }

  private get getContainerClassNames(): void | GetClassNames {
    return MediaControl.getContainerClassNames
  }

  private get isWithVerticalVolumeBar(): boolean {
    return !!this.$verticalVolume && this.$verticalVolume.length > 0
  }

  private get provider(): IFrameProvider | undefined {
    return this.core.options.provider
  }

  private get isMobileChrome(): boolean {
    return Browser.isMobile && Browser.isChrome
  }

  private get isMobileSafari(): boolean {
    return Browser.isiOS && Browser.isSafari
  }

  private get isLive(): boolean {
    return this.container?.getPlaybackType() === Playback.LIVE
  }

  private get isLiveDvr(): boolean {
    return this.isLive && !!this.core.options.useDvrControls
  }

  public bindEvents(): void {
    super.bindEvents()
    this.listenToOnce(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(this.core, Events.CORE_FULLSCREEN, this.onFullscreenChange)

    if (this.suggest) {
      this.listenTo(
        this.suggest,
        Events.SUGGEST_RENDERED,
        this.onSuggestRendered
      )
      this.listenTo(this.suggest, Events.SUGGEST_CLOSE, this.onSuggestClose)
    }

    const share = this.core.getPlugin('share')

    if (share) {
      this.listenTo(share, Events.SHARE_CLOSE, this.showControls)
    }

    const settingsMenu = this.core.getPlugin('settingsMenu')

    if (settingsMenu) {
      this.listenTo(settingsMenu, Events.SETTINGS_MENU_CLOSE, this.onMenuClose)
    }

    if (this.multiPlayer) {
      this.listenTo(
        this.multiPlayer,
        Events.MULTIPLAYER_RENDERED,
        this.onMultiplayerRendered
      )
    }

    const levelSelector = this.core.getPlugin('levelSelector')

    if (levelSelector) {
      this.listenTo(
        levelSelector,
        Events.LEVEL_SELECTOR_RENDERED,
        this.onLevelSelectorRendered
      )
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_LOADING,
        this.onAdLoading
      )
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
        this.onAdContentResumeRequested
      )
    }

    if (this.playback) {
      this.listenToOnce(
        this.playback,
        Events.PLAYBACK_PLAY_INTENT,
        this.onInitialPlay
      )
      this.listenToOnce(
        this.playback,
        Events.PLAYBACK_LOADEDDATA,
        this.updateVolumeUI
      )
    }

    if (this.docking) {
      this.listenTo(this.docking, Events.DOCKING_CHANGED, this.onParentResize)
    }

    window.addEventListener('resize', this.onParentResize)
    window.addEventListener('orientationchange', this.onParentResize)
  }

  public bindContainerEvents(): void {
    super.bindContainerEvents()

    if (this.playback) {
      this.listenTo(
        this.playback,
        Events.PLAYBACK_PLAY_INTENT,
        this.onPlayIntent
      )
    }

    if (this.container) {
      this.listenTo(this.container, Events.CONTAINER_PLAY, this.onPlay)
      this.listenTo(this.container, Events.CONTAINER_PAUSE, this.onPause)
      this.listenTo(this.container, Events.CONTAINER_SEEK, this.onSeek)
      this.listenTo(this.container, Events.CONTAINER_STOP, this.onStop)
      this.listenTo(this.container, Events.CONTAINER_ENDED, this.onEnded)
      // listen container el manually to call requestFullscreen by user gesture
      this.container.el.addEventListener(
        'dblclick',
        this.onContainerDoubleClick
      )
      this.stopListening(
        this.container,
        Events.CONTAINER_DBLCLICK,
        this.toggleFullscreen
      )
    }
  }

  private onReady(): void {
    const {
      rewind,
      autoPlay,
      showTitle,
      volumeVisible,
      fullscreenVisible,
      saveUserVolume,
      handledLiveErrorCode,
      [PLAY_IN_VISIBLE_PAGE]: playInVisiblePage,
      [PLAY_IN_VISIBLE_AREA]: playInVisibleArea,
      [UNMUTE_IN_VISIBLE_AREA]: unmuteInVisibleArea
    } = this.core.options

    this.$el
      .toggleClass('eplayer-rewind-hidden', rewind === false)
      .toggleClass('eplayer-title-hidden', showTitle !== true)
      .toggleClass('eplayer-volume-hidden', volumeVisible === false)
      .toggleClass('eplayer-quality-hidden', true)
      .toggleClass('eplayer-fullscreen-hidden', fullscreenVisible === false)
      .toggleClass(
        'eplayer-has-announce',
        handledLiveErrorCode === Errors.ANNOUNCE.code
      )
      .toggleClass('eplayer-handled-error', !!handledLiveErrorCode)

    const hasAutoPlay =
      unmuteInVisibleArea || playInVisibleArea || autoPlay || playInVisiblePage

    if (hasAutoPlay) {
      this.changeTogglePlay()
    }

    document.addEventListener(
      'touchmove',
      this.updateDrag,
      this.isMobileChrome && {passive: false}
    )
    document.addEventListener('touchend', this.stopDrag)
    document.addEventListener('mouseleave', this.onMouseLeave)

    if (saveUserVolume) {
      this.provider?.mediator?.on(
        'changeOptions:volume',
        this.onChannelVolumeChange
      )
    }

    this.injectStyles()
  }

  private async injectStyles(): Promise<void> {
    const style = document.querySelector(`style[data-scope="${this.name}"]`)

    if (style) {
      return
    }

    const {
      skinName,
      fontUrl,
      backgroundColor,
      foregroundColor,
      textColor,
      hoverColor
    } = this.params

    const textColorString = fade(
      convertNumberToHex(
        textColor || textColor === 0 ? textColor : WHITE_NUMBER
      ),
      1
    )
    const hoverColorString = fade(
      convertNumberToHex(
        hoverColor || hoverColor === 0 ? hoverColor : WHITE_NUMBER
      ),
      1
    )
    const backgroundColorString = fade(
      convertNumberToHex(
        backgroundColor || backgroundColor === 0 ? backgroundColor : 0
      ),
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      0.7
    )

    const foregroundColorHex = convertNumberToHex(
      foregroundColor || foregroundColor === 0 ? foregroundColor : WHITE_NUMBER
    )
    const foregroundColorString = fade(foregroundColorHex, 1)

    if (
      !skinName ||
      foregroundColorString !== WHITE_STRING ||
      backgroundColorString !== 'rgba(0, 0, 0, 0.70)' ||
      textColorString !== WHITE_STRING ||
      hoverColorString !== WHITE_STRING
    ) {
      const isForegroundColorDark = isDark(foregroundColorHex)

      this.core.$el
        .toggleClass('eplayer-dark-main-color', isForegroundColorDark)
        .toggleClass('eplayer-light-main-color', !isForegroundColorDark)

      injectStyle(
        `
          .eagleplayer {
            --skin-main-color: ${foregroundColorString};
            --skin-text-color: ${textColorString};
            --skin-hover-color: ${hoverColorString};
            --skin-bg-color: ${backgroundColorString};
          }
          .eplayer-main-color {color:${foregroundColorString}}
          .eplayer-main-color-background {background:${foregroundColorString}}
          .eplayer-text-color {color:${textColorString}}
          .eplayer-text-color-background {background:${textColorString}}
          .eplayer-hover-color:hover {color:${hoverColorString}}
          .eplayer-active-color {color:${hoverColorString}}
          .eplayer-active-bg-color {background:${hoverColorString}}
          .eplayer-skin-button {color:${foregroundColorString}}
          .eplayer-skin-button-active {color:${hoverColorString}}
          .eplayer-skin-button:hover {color:${hoverColorString}}
          .eagleplayer .active {color:${hoverColorString}}
          .eplayer-skin-pane {background:${backgroundColorString}}
          .eplayer-skin-volume-handler {background:${foregroundColorString}}
        `,
        this.name
      )
    }

    if (fontUrl?.includes('fonts.googleapis.com')) {
      const styleSheet = await loadStyle(fontUrl)

      injectStyle(
        styleSheet.replace(
          /font-family: '.+';/g,
          'font-family: eagle-default;'
        ),
        this.name
      )
    }
  }

  private onFullscreenChange = debounce((fullscreen: boolean): void => {
    if (!fullscreen) {
      this.provider?.mediator?.toggleFullscreen(false)
    }
  }, MediaControl.FULLSCREEN_THROTTLE_TIMOUT)

  private onParentResize = debounce(() => {
    this.applyContainerClassNames()
    this.pickBestFittedSeekBar()
  }, MediaControl.RESIZE_THROTTLE_TIMOUT)

  private onMouseLeave = (): void => {
    this.stopDrag()
  }

  public stopListening(
    object?: unknown,
    name?: string,
    callback?: (event: any) => void
  ): this {
    if (!callback) {
      this.container?.el.removeEventListener(
        'dblclick',
        this.onContainerDoubleClick
      )
      document.removeEventListener('touchmove', this.updateDrag)
      document.removeEventListener('touchend', this.stopDrag)
      document.removeEventListener('mouseleave', this.onMouseLeave)
      window.removeEventListener('resize', this.onParentResize)
      window.removeEventListener('orientationchange', this.onParentResize)

      if (this.onChannelVolumeChange) {
        this.provider?.mediator?.removeListener(
          'changeOptions:volume',
          this.onChannelVolumeChange
        )
      }
    }

    return super.stopListening(object, name, callback)
  }

  public toggleFullscreen(event?: any): void {
    if (!this.core.isFullscreen()) {
      this.provider?.mediator?.toggleFullscreen(true)
    }

    super.toggleFullscreen()
    this.trigger(Events.MEDIACONTROL_FULLSCREEN_CLICK, event)
  }

  public onContainerDoubleClick = (): void => {
    if (this.options.mediaControlVisible) {
      this.toggleFullscreen(true)
    }
  }

  public play(): void {
    super.play()
    this.trigger(Events.MEDIACONTROL_PLAYPAUSE_CLICK)
  }

  public pause(): void {
    super.pause()
    this.trigger(Events.MEDIACONTROL_PLAYPAUSE_CLICK)
  }

  public stop(): void {
    super.stop()
    this.trigger(Events.MEDIACONTROL_PLAYPAUSE_CLICK)
  }

  public togglePlayPause(): void {
    if (this.isLiveDvr && !this.container.isPlaying()) {
      this.toggleStreamStatus(!this.hasStarted)

      if (!this.hasStarted) {
        this.container.seek(this.container.getDuration())
      }
    }

    super.togglePlayPause()
    this.trigger(Events.MEDIACONTROL_PLAYPAUSE_CLICK)
  }

  public togglePlayStop(): void {
    super.togglePlayStop()
    this.trigger(Events.MEDIACONTROL_PLAYPAUSE_CLICK)
  }

  public onVolumeClick(event: any): void {
    super.onVolumeClick(event)
    this.trigger(Events.MEDIACONTROL_VOLUME_CLICK)
    this.saveUserVolume()
  }

  private saveUserVolume = debounce(async (): Promise<void> => {
    const {saveUserVolume} = this.core.options

    if (saveUserVolume && !this.muted) {
      await this.provider?.mediator?.setOptionStorage(
        'volume',
        Math.round(MediaControl.volume as number)
      )
    }
  }, MediaControl.SYNC_VOLUME_TIMEOUT)

  public onLevelSelectorClick(event: any): void {
    const {qualitySelect} = event.target.dataset

    this.trigger(Events.MEDIACONTROL_LEVEL_SELECTOR_CLICK, qualitySelect)
  }

  public playNext(): void {
    this.trigger(Events.WATCH_NEXT_CLICK)
    this.multiPlayer?.playNext()
  }

  public showPlayNext(): void {
    this.watchNext?.show()
  }

  public hidePlayNext(): void {
    this.watchNext?.hide()
  }

  public setInitialVolume(): void {
    let volume

    if (MediaControl.volume != null) {
      volume = MediaControl.volume
    } else if (this.options.mute) {
      volume = 0
    } else {
      volume = this.initialVolume
    }

    this.setVolume(volume)
  }

  private onMultiplayerRendered = (): void => {
    this.$el
      .find('[data-play-next]')
      .toggle(!!this.options.watchNext && !!this.multiPlayer?.nextRecord)
  }

  private onLevelSelectorRendered = (): void => {
    const {qualityVisible} = this.core.options

    this.$el.toggleClass('eplayer-quality-hidden', !qualityVisible)
  }

  private onSuggestRendered = (shouldToggle = true): void => {
    this.applyContainerClassNames()
    this.$el.toggleClass(this.classNames.skinWithSuggest, true)
    this.hideControls(shouldToggle)
  }

  private onSuggestClose = (): void => {
    this.applyContainerClassNames()
    this.$el.toggleClass(this.classNames.skinWithSuggest, false)
    this.showControls()
  }

  private onMenuClose = (): void => {
    this.preventShowControls = false
    this.showControls()
  }

  private showControls(): void {
    const {mediaControlVisible} = this.core.options

    if (!this.preventShowControls && mediaControlVisible) {
      this.$el.removeClass('eplayer-hide-skin')
    }
  }

  private hideControls = (shouldToggle = true): void => {
    if (shouldToggle) {
      this.$el.addClass('eplayer-hide-skin')
    }
  }

  public getVolumeFromUIEvent(event: MouseEvent | TouchEvent): number {
    const touch = 'changedTouches' in event ? event.changedTouches[0] : event
    let volumeFromUI: number

    if (this.isWithVerticalVolumeBar) {
      const offsetX = touch.pageY - this.$volumeBarContainer.offset().top

      volumeFromUI = 100 - (offsetX / this.$volumeBarContainer.height()) * 100
    } else {
      const offsetY = touch.pageX - this.$volumeBarContainer.offset().left

      volumeFromUI = (offsetY / this.$volumeBarContainer.width()) * 100
    }

    volumeFromUI = Math.min(volumeFromUI, 100)

    this.trigger(Events.MEDIACONTROL_VOLUME_CLICK, volumeFromUI)

    return volumeFromUI
  }

  public toggleMute(): void {
    const volume = this.muted ? this.mutedVolume || this.initialVolume : 0

    this.trigger(Events.MEDIACONTROL_MUTE_CLICK, volume)
    // pass isInitialVolume true to prevent saving toggling result to local storage
    this.setVolume(volume, volume === 0)
    this.saveUserVolume()
  }

  public setVolume(volume: number, isInitialVolume = false): void {
    this.mutedVolume =
      volume === 0 ? this.mutedVolume ?? MediaControl.volume : null

    if (!isInitialVolume) {
      MediaControl.volume = volume
    }

    super.setVolume(volume, isInitialVolume)
  }

  private onChannelVolumeChange = (value: number): void => {
    const {saveUserVolume} = this.core.options
    const currentVolume = this.muted ? this.mutedVolume : this.volume

    if (saveUserVolume && Math.round(currentVolume as number) !== value) {
      if (this.muted) {
        this.mutedVolume = value
      } else {
        this.setVolume(value)
      }
    }
  }

  public updateVolumeUI(): void {
    if (!this.rendered) {
      // this will be called after a render
      return
    }

    const {classNames, volume, hasAudio} = this

    // update volume bar scrubber/fill on bar mode
    this.$volumeBarFill.css({
      [this.isWithVerticalVolumeBar ? 'height' : 'width']: `${volume}%`
    })

    // update volume icon
    // TODO(VP-4598): Избавиться от использования классов состояния элементов
    // Использование глобальных классов состояния скина имеет приоритет
    this.$volumeIcon
      .toggleClass('volume-mute', !hasAudio || volume === 0)
      .toggleClass(
        'volume-low',
        hasAudio && volume > 0 && volume <= MediaControl.MEDIUM_VOLUME
      )
      .toggleClass(
        'volume-medium',
        hasAudio &&
          volume > MediaControl.MEDIUM_VOLUME &&
          volume < MediaControl.HIGH_VOLUME
      )
      .toggleClass(
        'volume-high',
        hasAudio && volume >= MediaControl.HIGH_VOLUME
      )

    this.$el
      .toggleClass(classNames.skinVolumeMute, !hasAudio || volume === 0)
      .toggleClass(
        classNames.skinVolumeLow,
        hasAudio && volume > 0 && volume <= MediaControl.MEDIUM_VOLUME
      )
      .toggleClass(
        classNames.skinVolumeMedium,
        hasAudio &&
          volume > MediaControl.MEDIUM_VOLUME &&
          volume < MediaControl.HIGH_VOLUME
      )
      .toggleClass(
        classNames.skinVolumeHigh,
        hasAudio && volume >= MediaControl.HIGH_VOLUME
      )
      .toggleClass(classNames.skinNoSound ?? 'eplayer-skin-no-sound', !hasAudio)
  }

  public changeTogglePlay(): void {
    this.trigger(
      this.container?.isPlaying() || this.isPlaying
        ? Events.MEDIACONTROL_PLAYING
        : Events.MEDIACONTROL_NOTPLAYING
    )
  }

  private updateTogglePlayUI(state?: 'playing' | 'paused' | 'ended'): void {
    // TODO(VP-4598): Избавиться от использования классов состояния элементов
    // Использование глобальных классов состояния скина имеет приоритет
    const {classNames} = this
    const playing = state === 'playing'
    const paused = state === 'paused'
    const ended = state === 'ended'

    this.$playPauseToggle
      .toggleClass('eplayer-skin-toggle-playing', playing)
      .toggleClass('eplayer-skin-toggle-pause', paused)
      .toggleClass('eplayer-skin-toggle-ended', ended)
    this.$el
      .toggleClass(classNames.skinPlaying, playing)
      .toggleClass(classNames.skinPaused, paused)
      .toggleClass(classNames.skinEnded, ended)
      .toggleClass(classNames.skinStopped, paused || ended)
  }

  private onAdLoading(event: AdvertisementEvent): void {
    const hideControls =
      event.blockType !== AdvertisementBlockType.BANNER &&
      !this.advertisement?.isPreRollWithStartTime(event.blockType) &&
      event.blockType !== AdvertisementBlockType.MIDDLE_ROLL

    if (hideControls) {
      this.hideControls()
    }
  }

  private onAdContentResumeRequested(event: AdvertisementEvent): void {
    const postRoll = event.blockType === AdvertisementBlockType.POST_ROLL

    this.showControls()

    if (postRoll) {
      this.onStop()
    }
  }

  private onInitialPlay(): void {
    this.isPlaying = true
    this.isEnded = false
    this.hideControls()
  }

  private onPlayIntent(): void {
    this.updateTogglePlayUI('playing')
  }

  private onPlay(): void {
    this.hasStarted = true
    this.isStopped = false
    this.isPlaying = true
    this.isEnded = false
    this.$endedHidden.show()
    this.showControls()
    this.updateTogglePlayUI('playing')
    this.$el.toggleClass('eplayer-skin-not-started', false)
  }

  private onPause(): void {
    this.isPlaying = false
    this.updateTogglePlayUI('paused')
  }

  private onInitialSeek = (): void => {
    this.hasStarted = true
    this.isPlaying = false
    this.isStopped = false
    this.isEnded = false
    this.$endedHidden.show()
    this.showControls()
    this.updateTogglePlayUI('paused')
    this.$el.toggleClass('eplayer-skin-not-started', false)
  }

  private onSeek(time: number): void {
    if (this.isLive && !this.hasStarted) return

    if (this.isLiveDvr && this.isPlaying) {
      this.toggleStreamStatus(time + 1 >= this.container.getDuration())
    }

    if (!this.hasStarted) {
      this.onInitialSeek()
    }
  }

  private onStop(): void {
    this.isStopped = true
    this.isPlaying = false
    this.$seekBarLoaded.css({left: 0, width: 0})
    this.$el.css({'--seekbar-loaded-left': '0', '--seekbar-loaded': '0'})
    this.updateTogglePlayUI('ended')
  }

  private onEnded(): void {
    this.isEnded = true
    this.$endedHidden.hide()
    this.updateTogglePlayUI('ended')

    window.setTimeout(() => {
      // После окончания проигрывания, позиция меняется на 0 сек.
      // Отложим изменение значения, чтобы предотвратить вызов `onInitialSeek` в этот момент.
      this.hasStarted = false
    }, MediaControl.END_TIMEOUT)
  }

  public mousemoveOnSeekBar(event: MouseEvent | TouchEvent): void {
    this.trigger(Events.MEDIACONTROL_MOUSEMOVE_SEEKBAR, event)
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public playerResize(): void {}

  public startSeekDrag(event: MouseEvent | TouchEvent): void {
    if (!this.settings.seekEnabled) {
      return
    }

    this.trigger(Events.MEDIACONTROL_SEEK_START, event)
    this.$el.toggleClass(this.classNames.activeSeekBar, true)
    this.draggingSeekBar = true

    if (!this.isMobileChrome) {
      event.preventDefault()
    }
  }

  public startVolumeDrag(event: MouseEvent | TouchEvent): void {
    this.trigger(Events.MEDIACONTROL_VOLUME_MOUSEDOWN)

    this.$el.toggleClass(this.classNames.activeVolumeBar, true)
    this.draggingVolumeBar = true
    event.preventDefault()
  }

  public updateDrag = throttle((event: MouseEvent | TouchEvent): void => {
    if (this.draggingSeekBar) {
      event.preventDefault()
      this.trigger(Events.MEDIACONTROL_SEEK, event)

      const touch = 'changedTouches' in event ? event.changedTouches[0] : event
      const offsetX = touch.pageX - this.$seekBarContainer.offset().left
      let pos = (offsetX / this.$seekBarContainer.width()) * 100

      pos = Math.min(100, Math.max(pos, 0))
      this.setSeekPercentage(pos)
      this.seek(event)
    } else if (this.draggingVolumeBar) {
      event.preventDefault()
      this.setVolume(this.getVolumeFromUIEvent(event))
      this.saveUserVolume()
    }
  })

  public stopDrag = (event?: MouseEvent | TouchEvent): void => {
    if (this.draggingSeekBar) {
      this.$el.toggleClass(this.classNames.activeSeekBar, false)
      this.trigger(Events.MEDIACONTROL_SEEK_END)

      if (event) {
        this.seek(event)
      }
    }

    if (this.draggingVolumeBar) {
      this.$el.toggleClass(this.classNames.activeVolumeBar, false)
    }

    this.draggingSeekBar = false
    this.draggingVolumeBar = false
  }

  public setSeekPercentage(value: number): void {
    const normalizedValue = value ? Math.max(Math.min(value, 100.0), 0) : 0

    if (this.displayedSeekBarPercentage === normalizedValue) {
      // not changed since last update
      return
    }

    this.displayedSeekBarPercentage = normalizedValue
    this.$seekBarPosition.css({width: `${normalizedValue}%`})
    this.$el.css({'--seekbar-position': `${normalizedValue / 100}`})
  }

  public seek(event: MouseEvent | TouchEvent): void | boolean {
    if (!this.settings.seekEnabled) {
      return
    }

    const touch = 'changedTouches' in event ? event.changedTouches[0] : event
    const offsetX = touch.pageX - this.$seekBarContainer.offset().left
    let pos = (offsetX / this.$seekBarContainer.width()) * 100

    pos = Math.min(100, Math.max(pos, 0))
    this.container?.seekPercentage(pos)
    this.setSeekPercentage(pos)

    if (this.isEnded) {
      this.play()
    }

    return false
  }

  private toggleStreamStatus(isActive: boolean): void {
    this.$el.toggleClass('eplayer-live-history', !isActive)
    this.$liveButton
      .toggleClass('eplayer-skin-live-clickable', !isActive)
      .find('span')
      .html(isActive ? 'Прямой эфир' : 'Вернуться к прямому эфиру')
  }

  public backToLiveStream(): void {
    const isPaused = !this.isPlaying && !this.isEnded && !this.isStopped

    this.toggleStreamStatus(true)
    this.container.seek(this.container.getDuration())

    if (isPaused) {
      this.play()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public highDefinitionUpdate(): void {}

  public updateProgressBar(progress: {
    start: number
    current: number
    total: number
  }): void {
    const loadedStart = progress.start / progress.total
    const loadedEnd =
      Math.min(progress.current, progress.total) / progress.total

    this.$seekBarLoaded.css({
      left: `${loadedStart * 100}%`,
      width: `${(loadedEnd - loadedStart) * 100}%`
    })
    this.$el.css({
      '--seekbar-loaded-left': `${loadedStart}`,
      '--seekbar-loaded': `${loadedEnd}`
    })
  }

  private updateActiveEpisode(position: number): void {
    const {episodes} = this.options as any
    const {classNames, $episodes} = this

    if (episodes?.length && $episodes.length) {
      let index = episodes.length - 1

      for (let i = 0; i < episodes.length; i++) {
        const item = episodes[i]

        if (item.start <= position && position < item.end) {
          index = i
          break
        }
      }

      const $activeEpisode = $episodes.eq(index)

      if (!$activeEpisode.hasClass(classNames.activeEpisode)) {
        $episodes.toggleClass(classNames.activeEpisode, false)
        $activeEpisode.toggleClass(classNames.activeEpisode, true)
      }
    }
  }

  public onTimeUpdate(timeProgress: {current: number; total: number}): void {
    const {current, total} = timeProgress
    const resultCurrent = Math.min(current, total)
    const position = resultCurrent < 0 ? total : Math.min(resultCurrent, total)
    const leftTime = resultCurrent < 0 ? 0 : total - resultCurrent

    this.updateActiveEpisode(position)

    if (this.draggingSeekBar) {
      this.updateTimeIndicator(position, leftTime)

      return
    }

    if (this.isLiveDvr) {
      const finiteDurations = [
        current,
        total,
        this.isMobileSafari ? this.currentDurationValue : Infinity
      ].filter(isFinite)

      this.currentDurationValue = Math.max(...finiteDurations)
    } else {
      this.currentDurationValue = total
    }

    this.currentPositionValue = position
    this.currentLeftTimeValue = leftTime
    this.renderSeekBar()
  }

  public renderSeekBar(): void {
    if (
      this.currentPositionValue === null ||
      this.currentDurationValue === null ||
      this.currentLeftTimeValue == null
    )
      return

    let currentSeekBarPercentage = 100

    if (!this.isLive || this.isLiveDvr) {
      currentSeekBarPercentage =
        (this.currentPositionValue / this.currentDurationValue) * 100
    }

    this.setSeekPercentage(currentSeekBarPercentage)

    const newPosition = Utils.formatTime(this.currentPositionValue)
    const newDuration = Utils.formatTime(this.currentDurationValue)
    const newLeftTime = Utils.formatTime(this.currentLeftTimeValue)
    const newPlaceholder = newDuration.replace(/\d/g, '0')

    if (newPosition !== this.displayedPosition) {
      this.$position.text(newPosition)
      this.displayedPosition = newPosition
    }

    if (newDuration !== this.displayedDuration) {
      this.$duration.text(newDuration)
      this.displayedDuration = newDuration
      this.$el.css('--duration', String(this.currentDurationValue))
    }

    if (newPlaceholder !== this.displayedTimePlaceholder) {
      this.$timePlaceholder.text(newPlaceholder)
      this.displayedTimePlaceholder = newPlaceholder
    }

    if (newLeftTime !== this.displayedLeftTime) {
      this.$leftTime.text(newLeftTime)
      this.displayedLeftTime = newLeftTime
    }
  }

  private pickBestFittedSeekBar(): void {
    // NOTE: fix lenta/unity skins that has 2 separated desktop/mobile seek bars
    if (this.$initialSeekBarContainers?.length > 1) {
      const isExtraSmall =
        this.core.activeContainer?.el.offsetWidth < MOBILE_MAX_WIDTH

      this.$initialSeekBarContainers.forEach((container) => {
        const isFittedToCurrentWidth = $(container)
          .parent()
          .is(
            isExtraSmall
              ? '.eplayer-skin-seek-unhover'
              : ':not(.eplayer-skin-seek-unhover)'
          )

        if (isFittedToCurrentWidth) {
          this.$seekBarContainer = $(container).attr('data-seek', '')
        } else {
          $(container).removeAttr('data-seek')
        }
      })
    }
  }

  private applyContainerClassNames(): void {
    this.$el.css({
      '--player-height': this.core.el.offsetHeight,
      '--player-width': this.core.el.offsetWidth
    })

    this.getContainerClassNames?.({
      width: this.core.el.offsetWidth,
      height: this.core.el.offsetHeight,
      suggestHeight: this.suggest?.height ?? null,
      docking: !!this.docking?.isDocked,
      title: !!this.core.options.showTitle,
      likes: !!this.core.options.likes,
      episodes: !!this.core.options.episodes?.length
    })?.forEach((entry) => {
      this.core.$el.toggleClass(...entry)
    })
  }

  public createCachedElements(): void {
    // TODO(VP-4598): Избавиться от использования классов для поиска элементов
    // Использование дата аттрибутов имеет приоритет
    this.$duration = this.$el.find(
      '.eplayer-skin-timeinfo-total, [data-time-total]'
    )
    this.$position = this.$el.find(
      '.eplayer-skin-timeinfo-current, [data-time-current]'
    )
    this.$leftTime = this.$el.find('[data-time-left]')
    this.$timePlaceholder = this.$el.find('[data-time-placeholder]')
    this.$liveButton = this.$el.find('.eplayer-skin-live')
    this.$fullscreenToggle = this.$el.find('.eplayer-skin-button-fullscreen')
    this.$playPauseToggle = this.$el.find('.eplayer-skin-toggle')
    this.$seekBarContainer = this.$el.find('[data-seek]')
    this.$seekBarLoaded = this.$el.find(
      '.eplayer-skin-seek-loaded, [data-seek-loaded]'
    )
    this.$seekBarPosition = this.$el.find(
      '.eplayer-skin-seek-bar, [data-seek-bar]'
    )
    this.$episodes = this.$el.find('[data-seek-episode]')
    this.$volumeBarContainer = this.$el.find(
      '.eplayer-skin-volume-pane, [data-volume]'
    )
    this.$volumeIcon = this.$el.find('.eplayer-skin-volume-button')
    this.$volumeBarFill = this.$el.find(
      '.eplayer-skin-volume-bar, [data-volume-bar]'
    )
    this.$verticalVolume = this.$el.find(
      '.eplayer-vertical-volume, [data-volume-vertical]'
    )
    this.$title = this.$el.find('.eplayer-skin-title-text, [data-title]')
    this.$endedHidden = this.$el.find('.eplayer-skin-ended-unvisible')
    this.resetIndicators()
  }

  public resetIndicators(): void {
    super.resetIndicators()
    this.displayedTimePlaceholder = this.$timePlaceholder.first().text()
    this.displayedLeftTime = this.$leftTime.text()
  }

  public onSubtitleClick(event: MouseEvent): void {
    const {key} = (event.target as HTMLElement).dataset

    this.trigger(Events.MEDIACONTROL_SUBTITLE_CHANGED, key)
  }

  public onRewindClick(event: MouseEvent): void {
    const {rewind: direction} = (event.target as HTMLElement).dataset
    let shift = 0

    /* eslint-disable @typescript-eslint/no-magic-numbers */
    if (direction === 'backward') {
      shift = -5
    } else if (direction === 'forward') {
      shift = 5
    }
    /* eslint-enable @typescript-eslint/no-magic-numbers */

    if (direction && shift) {
      const duration = this.container.getDuration()
      const currentTime = this.container.getCurrentTime()
      const time = Math.max(0, Math.min(currentTime + shift, duration))

      if (time !== currentTime) {
        this.container.seek(time)
        this.trigger(Events.MEDIACONTROL_REWIND_CLICK, {direction})
      }
    }
  }

  public onShareClick(): void {
    this.trigger(Events.MEDIACONTROL_SHARE)

    // pass to next tick to prevent hide before showing
    window.setTimeout(this.hideControls, 0)
  }

  protected onMenuClick(event: MouseEvent): void {
    this.trigger(
      Events.MEDIACONTROL_MENU_CLICK,
      (event.currentTarget as HTMLElement).dataset.menu
    )
  }

  public onTitleClick(): void {
    const {clickOnTitle, playList} = this.core.options
    const sourceUrl = playList?.clickUrl || playList?.defaultShareUrl

    if (!clickOnTitle || !sourceUrl) {
      return
    }

    const clickUrl = sourceUrl.match(/^https?:\/\//i)
      ? sourceUrl
      : `https://${sourceUrl.replace(/^\/\//, '')}`

    window.open(clickUrl, '_blank')
  }

  public updateTitleUI(): void {
    const {showTitle, clickOnTitle, playList} = this.core.options

    if (showTitle && playList?.title) {
      this.$title?.text(playList.title)
    }

    const hasUrlToOpen = playList?.clickUrl || playList?.defaultShareUrl

    if (!clickOnTitle || !hasUrlToOpen) {
      this.$title?.css('pointer-events', 'none')
    }
  }

  public configure(options: Partial<T>): void {
    if (!this.advertisement?.hasAd()) {
      super.configure(options)
      this.enable()
    }
  }

  public updateTimeIndicator(position: number, leftTime: number): void {
    this.$position.text(Utils.formatTime(position))
    this.$leftTime.text(Utils.formatTime(leftTime))
  }

  public getSettings(): PlaybackSettings & {id?: string | number} {
    // Добавить id в settings
    // для вызова метода render после события CORE_ACTIVE_CONTAINER_CHANGED
    // В этом случае settingsChanged в методе settingsUpdate будет = true
    return $.extend(true, {id: this.core.options.id}, this.container?.settings)
  }

  public settingsUpdate(): void {
    this.container.settings.seekEnabled = !!this.playback.isSeekEnabled?.()
    super.settingsUpdate()
  }

  public render(): this {
    const {
      mediaControlVisible,
      share,
      seekBarVisible = true,
      playList
    } = this.core.options

    const duration = (playList?.duration ?? 0) / 1000

    this.$el.css('--duration', String(duration))
    this.$el.html(
      this.template({
        live: this.isLive,
        episodes: (this.options as any).episodes ?? [{start: 0, end: duration}],
        i18n: Object.fromEntries(
          [
            'share',
            'settings',
            'quality',
            'speed',
            'subtitles',
            'live_back_to',
            'replay'
          ].map((key) => [key, this.core.i18n.t(key)])
        )
      })
    )

    if (this.hideControls && !mediaControlVisible) {
      this.hideControls()
    }

    this.$initialSeekBarContainers = this.$el.find('[data-seek]')

    if (!seekBarVisible) {
      this.$initialSeekBarContainers.remove()
      this.$el.find('[data-progress]').remove()
    }

    this.pickBestFittedSeekBar()
    this.createCachedElements()

    let previousSeekPercentage = 0

    if (this.displayedSeekBarPercentage) {
      previousSeekPercentage = this.displayedSeekBarPercentage
    }

    this.displayedSeekBarPercentage = null
    this.setSeekPercentage(previousSeekPercentage)
    this.$el.find('.eplayer-share-copy-trailer-screen').remove()

    if (!share) {
      this.$el.find('[data-share], .eplayer-skin-share').remove()
    }

    if (!this.isLiveDvr) {
      this.$liveButton.remove()
    }

    this.core.$el.append(this.el)
    this.rendered = true
    this.updateVolumeUI()
    this.updateTitleUI()

    this.renderSeekBar()
    this.applyContainerClassNames()

    if (this.core.isReady) {
      this.changeTogglePlay()
    }

    this.trigger(Events.MEDIACONTROL_RENDERED)

    return this
  }
}
